import {
  useIsMobile,
  WSButton,
  WSCard,
  WSControlGroup,
  WSElement,
  WSFlexBox,
  WSForm,
  WSInputDate,
  WSInputMask,
  WSInputText,
  WSList,
  WSText
} from "@wingspanhq/fe-component-library";
import { useModalSsnInfo } from "../../routes/RouteUsPersonInfo/steps/useModalSsnInfo";

export type FormPartialDataUsPersonInfo = {
  ssn?: string;
  firstName: string;
  middleName?: string;
  lastName: string;
  usPersonSince: "always" | "date";
  usPersonDate?: Date;
};

type Props = {
  name: string;
  mismatch?: boolean;
};

export const FormPartialUsPersonInfo: React.FC<Props> = ({
  name,
  mismatch
}) => {
  const isMobile = useIsMobile();
  const modalSsnInfo = useModalSsnInfo();

  const mismatchProps = mismatch
    ? ({
        status: "warning",
        message: "Mismatched info"
      } as const)
    : {};

  return (
    <WSCard
      header={{
        label: "U.S. person information",
        value: {
          pill: {
            theme: "warning",
            text: "Requested update"
          }
        }
      }}
      divider
    >
      <WSList gap="XL">
        <WSText.ParagraphSm color="gray500">
          You&apos;ve indicated that you are a U.S. person and you will need to
          provide your U.S. information for tax reporting purposes.
        </WSText.ParagraphSm>

        <WSElement>
          <WSForm.Field
            name={name + ".ssn"}
            label="Social Security Number (SSN)"
            component={WSInputMask}
            componentProps={{
              required: true,
              placeholder: "XXX-XX-XXXX",
              mask: "999-99-9999",
              unmask: true,
              fsExclude: true,
              ...mismatchProps
            }}
          />
          <WSButton.Link
            mt="S"
            type="button"
            onClick={() => {
              modalSsnInfo.open();
            }}
          >
            What is this used for?
          </WSButton.Link>
        </WSElement>

        <WSText.ParagraphSm color="gray500">
          Spell your first and last name exactly as shown on your U.S.
          government-issued ID.
        </WSText.ParagraphSm>

        <WSFlexBox
          direction={isMobile ? "column" : "row"}
          wrap="nowrap"
          gap="L"
        >
          <WSForm.Field
            name={name + ".firstName"}
            label="Legal first name"
            component={WSInputText}
            componentProps={{
              required: true,
              placeholder: "Enter first name",
              fsExclude: true,
              autoComplete: "given-name",
              ...mismatchProps
            }}
          />

          <WSForm.Field
            name={name + ".middleName"}
            label="Middle name"
            component={WSInputText}
            componentProps={{
              placeholder: "Enter middle name",
              fsExclude: true,
              autoComplete: "additional-name"
            }}
          />

          <WSForm.Field
            name={name + ".lastName"}
            label="Legal last name"
            component={WSInputText}
            componentProps={{
              required: true,
              placeholder: "Enter last name",
              fsExclude: true,
              autoComplete: "family-name",
              ...mismatchProps
            }}
          />
        </WSFlexBox>

        <WSForm.Field
          name={name + ".usPersonSince"}
          label="When did you become a U.S. person?"
          component={WSControlGroup}
          componentProps={{
            type: "radio",
            options: [
              {
                label: "I've always been a U.S. person",
                value: "always",
                helperText: "Citizen from birth"
              },
              {
                label: "I became a U.S. person on this date:",
                value: "date",
                helperText: "Naturalization, Green Card, tax resident"
              }
            ]
          }}
        />

        <WSForm.Value name={name + ".usPersonSince"}>
          {(usPersonSince: string) =>
            usPersonSince === "date" ? (
              <WSForm.Field
                name={name + ".usPersonDate"}
                label="Date you became a U.S. person"
                component={WSInputDate}
                componentProps={{
                  required: true
                }}
              />
            ) : null
          }
        </WSForm.Value>
      </WSList>
    </WSCard>
  );
};
